import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Rating from '@mui/material/Rating';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';

import { useLocale } from '@/i18n/LocaleContext';
import type { CustomerReview } from '@/services/api/types';

/** One native customer review — shared between the landing summary and the full reviews page. */
export function ReviewCard({ review }: { review: CustomerReview }) {
  const { t } = useTranslation();
  const locale = useLocale();

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Stack spacing={1.5}>
          <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
            <Avatar>{review.customerName.charAt(0).toUpperCase()}</Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle2">{review.customerName}</Typography>
              <Typography variant="caption" color="text.secondary">
                {review.originName} → {review.destinationName}
                {review.pickupAt && ` · ${new Date(review.pickupAt).toLocaleDateString(locale)}`}
              </Typography>
            </Box>
          </Stack>

          {review.averageRating !== undefined && (
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <Rating value={review.averageRating} precision={0.5} size="small" readOnly />
              <Typography variant="body2" color="text.secondary">
                {review.averageRating.toFixed(1)}
              </Typography>
            </Stack>
          )}

          <Stack direction="row" spacing={2} useFlexGap sx={{ flexWrap: 'wrap' }}>
            <RatingChip label={t('reviews.categories.punctuality')} value={review.punctuality} />
            <RatingChip label={t('reviews.categories.cleanliness')} value={review.cleanliness} />
            <RatingChip label={t('reviews.categories.service')} value={review.service} />
          </Stack>

          {review.description && (
            <Typography
              variant="body2"
              sx={{
                color: (theme) => theme.custom.quote,
                display: '-webkit-box',
                WebkitLineClamp: 5,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {review.description}
            </Typography>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}

function RatingChip({ label, value }: { label: string; value: number | undefined }) {
  if (value === undefined) return null;

  return (
    <Typography variant="caption" color="text.secondary">
      {label}: {value.toFixed(1)}
    </Typography>
  );
}

export default ReviewCard;
